import { Grid, TextField } from '@material-ui/core';
import { t } from 'i18next';
import { ChangeEvent, useEffect, useState } from 'react';
import { ModalWithButtons } from '../../../../components/ModalWithButtons/ModalWithButtons.component';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { updateExaminerIndividualLimits } from '../../../../services/exams/examSheet/examSheet.service';
import { useStyles } from './ExaminerIndividualBlockingLimits.styles';
import { ExaminerIndividualBlockingLimits, LimitType } from './ExaminerIndividualBlockingLimits.types';
import TooltipOne from '../../../../components/TooltipOne/TooltipOne.component';
import SwitchOne from '../../../../components/SwitchOne/SwitchOne.component';

interface ExaminerIndividualBlockingLimitsProps {
  limits?: ExaminerIndividualBlockingLimits;
  onClose: () => void;
  onCancel: () => void;
}

enum ErrorMessage {
  totalLimitErrorMessage = 'Wartość limitu całkowitego musi być większa niż ustawiona wartość limitu sekwencyjnego',
  totalLimitOutsideAllowedRange = 'Podana wartość jest nieprawidłowa. Prawidłowa wartość powinna mieścić się w zakresie liczb 1-20',
  sequentialErrorMessage = 'Wartość limitu sekwencyjnego musi być mniejsza niż ustawiona wartość limitu całkowitego',
  sequentialLimitOutsideAllowedRange = 'Podana wartość jest nieprawidłowa. Prawidłowa wartość powinna mieścić się w zakresie liczb 2-20',
}

const MIN_TOTAL_LIMIT = 1;
const MIN_SEQUENTIAL_LIMIT = 2;
const MAX_LIMIT = 20;

export const EditExaminerIndividualBlockingLimits: React.FC<ExaminerIndividualBlockingLimitsProps> = (props) => {
  const classes = useStyles();

  const { openAlert } = useLayoutContext();

  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const [totalLimitOutOfTolerance, setTotalLimitOutOfTolerance] = useState<number | null>(
    props.limits?.totalLimitOutOfTolerance ?? null,
  );
  const [sequentialLimitOutOfTolerance, setSequentialLimitOutOfTolerance] = useState<number | null>(
    props.limits?.sequentialLimitOutOfTolerance ?? null,
  );
  const [totalLimitWithinTolerance, setTotalLimitWithinTolerance] = useState<number | null>(
    props.limits?.totalLimitWithinTolerance ?? null,
  );
  const [sequentialLimitWithinTolerance, setSequentialLimitWithinTolerance] = useState<number | null>(
    props.limits?.sequentialLimitWithinTolerance ?? null,
  );

  const [isTotalLimitOutOfTolerance, setIsTotalLimitOutOfTolerance] = useState<boolean>(
    !!props.limits?.totalLimitOutOfTolerance,
  );
  const [isSequentialLimitOutOfTolerance, setIsSequentialLimitOutOfTolerance] = useState<boolean>(
    !!props.limits?.sequentialLimitOutOfTolerance,
  );
  const [isTotalLimitWithinTolerance, setIsTotalLimitWithinTolerance] = useState<boolean>(
    !!props.limits?.totalLimitWithinTolerance,
  );
  const [isSequentialLimitWithinTolerance, setIsSequentialLimitWithinTolerance] = useState<boolean>(
    !!props.limits?.sequentialLimitWithinTolerance,
  );

  const [isTotalLimitOutOfToleranceError, setIsTotalLimitOutOfToleranceError] = useState<boolean>(false);
  const [isSequentialLimitOutOfToleranceError, setIsSequentialLimitOutOfToleranceError] = useState<boolean>(false);
  const [isTotalLimitWithinToleranceError, setIsTotalLimitWithinToleranceError] = useState<boolean>(false);
  const [isSequentialLimitWithinToleranceError, setIsSequentialLimitWithinToleranceError] = useState<boolean>(false);

  const inRange = (value: number, min: number, max: number) => {
    return value >= min && value <= max;
  };

  const nullOutOfToleranceLimits = totalLimitOutOfTolerance !== null && sequentialLimitOutOfTolerance !== null;
  const nullWithinToleranceLimits = totalLimitWithinTolerance !== null && sequentialLimitWithinTolerance !== null;
  const outOfToleranceValidation =
    nullOutOfToleranceLimits && totalLimitOutOfTolerance <= sequentialLimitOutOfTolerance;
  const withinToleranceValidation =
    nullWithinToleranceLimits && totalLimitWithinTolerance <= sequentialLimitWithinTolerance;

  const isTotalLimitOutOfToleranceNotInRange =
    totalLimitOutOfTolerance !== null && !inRange(totalLimitOutOfTolerance, MIN_TOTAL_LIMIT, MAX_LIMIT);
  const isSequentialLimitOutOfToleranceNotInRange =
    sequentialLimitOutOfTolerance !== null && !inRange(sequentialLimitOutOfTolerance, MIN_SEQUENTIAL_LIMIT, MAX_LIMIT);
  const isTotalLimitWithinToleranceNotInRange =
    totalLimitWithinTolerance !== null && !inRange(totalLimitWithinTolerance, MIN_TOTAL_LIMIT, MAX_LIMIT);
  const isSequentialLimitWithinToleranceNotInRange =
    sequentialLimitWithinTolerance !== null &&
    !inRange(sequentialLimitWithinTolerance, MIN_SEQUENTIAL_LIMIT, MAX_LIMIT);

  const isAnyItemOutOfRange =
    isTotalLimitOutOfToleranceNotInRange ||
    isSequentialLimitOutOfToleranceNotInRange ||
    isTotalLimitWithinToleranceNotInRange ||
    isSequentialLimitWithinToleranceNotInRange;

  useEffect(() => {
    setIsTotalLimitOutOfToleranceError(outOfToleranceValidation);
  }, [totalLimitOutOfTolerance]);

  useEffect(() => {
    setIsSequentialLimitOutOfToleranceError(outOfToleranceValidation);
  }, [sequentialLimitOutOfTolerance]);

  useEffect(() => {
    setIsTotalLimitWithinToleranceError(withinToleranceValidation);
  }, [totalLimitWithinTolerance]);

  useEffect(() => {
    setIsSequentialLimitWithinToleranceError(withinToleranceValidation);
  }, [sequentialLimitWithinTolerance]);

  const handleTotalLimitOutOfToleranceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTotalLimitOutOfTolerance(e.target.value ? parseInt(e.target.value) : null);
  };

  const handleSequentialLimitOutOfTolerance = (e: ChangeEvent<HTMLInputElement>) => {
    setSequentialLimitOutOfTolerance(e.target.value ? parseInt(e.target.value) : null);
  };

  const handleTotalLimitWithinTolerance = (e: ChangeEvent<HTMLInputElement>) => {
    setTotalLimitWithinTolerance(e.target.value ? parseInt(e.target.value) : null);
  };

  const handleSequentialLimitWithinTolerance = (e: ChangeEvent<HTMLInputElement>) => {
    setSequentialLimitWithinTolerance(e.target.value ? parseInt(e.target.value) : null);
  };

  const getErrorRow = (errorMessage: ErrorMessage) => {
    return <div className={classes.errorMessage}>{errorMessage}</div>;
  };

  useEffect(() => {
    if (!props.limits) return;
    if (isTotalLimitOutOfTolerance === null) {
      setTotalLimitOutOfTolerance(null);
    }
    if (isSequentialLimitOutOfTolerance === null) {
      setSequentialLimitOutOfTolerance(null);
    }
    if (isTotalLimitWithinToleranceError === null) {
      setTotalLimitWithinTolerance(null);
    }
    if (isSequentialLimitWithinToleranceError === null) {
      setSequentialLimitWithinTolerance(null);
    }
  }, [
    props.limits,
    isTotalLimitOutOfTolerance,
    isSequentialLimitOutOfTolerance,
    isTotalLimitWithinToleranceError,
    isSequentialLimitWithinToleranceError,
  ]);

  const handleUpdateExaminerIndividualLimits = () => {
    if (props.limits && props.limits.examinerPermissionId) {
      updateExaminerIndividualLimits(
        props.limits.examinerPermissionId,
        totalLimitOutOfTolerance,
        sequentialLimitOutOfTolerance,
        totalLimitWithinTolerance,
        sequentialLimitWithinTolerance,
      ).then(
        () => {
          openAlert('success');
          props.onClose();
        },
        () => {
          openAlert('error');
          props.onCancel();
        },
      );
    }
  };

  const isDisabled = () => {
    return (
      isTotalLimitOutOfToleranceError ||
      isSequentialLimitOutOfToleranceError ||
      isTotalLimitWithinToleranceError ||
      isSequentialLimitWithinToleranceError ||
      isAnyItemOutOfRange
    );
  };

  useEffect(() => {
    if (
      totalLimitOutOfTolerance !== props.limits?.totalLimitOutOfTolerance ||
      sequentialLimitOutOfTolerance !== props.limits?.sequentialLimitOutOfTolerance ||
      totalLimitWithinTolerance !== props.limits?.totalLimitWithinTolerance ||
      sequentialLimitWithinTolerance !== props.limits?.sequentialLimitWithinTolerance ||
      isTotalLimitOutOfTolerance !== !!props.limits?.totalLimitOutOfTolerance ||
      isSequentialLimitOutOfTolerance !== !!props.limits?.sequentialLimitOutOfTolerance ||
      isTotalLimitWithinTolerance !== !!props.limits?.totalLimitWithinTolerance ||
      isSequentialLimitWithinTolerance !== !!props.limits?.sequentialLimitWithinTolerance
    ) {
      setIsDataChanged(true);
    } else {
      setIsDataChanged(false);
    }
  }, [
    totalLimitOutOfTolerance,
    sequentialLimitOutOfTolerance,
    totalLimitWithinTolerance,
    sequentialLimitWithinTolerance,
    isTotalLimitOutOfTolerance,
    isSequentialLimitOutOfTolerance,
    isTotalLimitWithinTolerance,
    isSequentialLimitWithinTolerance,
  ]);

  const examiner = `${props.limits?.lastName} ${props.limits?.firstName} (${props.limits?.examinerNumber})`;

  const title =
    props.limits?.blockingLimitsType === LimitType.globalLimit
      ? `Zdefiniuj limity indywidualne dla: ${examiner}`
      : `Edytuj limity indywidualne dla: ${examiner}`;

  return (
    <>
      <ModalWithButtons
        xCloseButton={props.onClose}
        open={true}
        size="md"
        title={title}
        autoHeight
        onCancel={props.onCancel}
        confirmButtonLabel="Zapisz"
        onConfirm={handleUpdateExaminerIndividualLimits}
        confirmButtonDisabled={isDisabled() || !isDataChanged}
        buttonsContainerStyle={{ marginTop: '20px' }}
        handleClose={props.onClose}
      >
        <Grid item xs={12} className={classes.subHeadersRow}>
          <Grid item xs={4} className={classes.subHeader}>
            <div className={classes.subHeader}>Rodzaj limitu</div>
          </Grid>
          <Grid item xs={4} className={classes.subHeader}>
            <div className={classes.subHeader}>Limit egzaminatora</div>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <hr className={classes.line}></hr>
        </Grid>
        <Grid container>
          <Grid item className={classes.item}>
            <Grid item xs={8}>
              <div className={classes.limitName}>Całkowity limit poza tolerancją</div>
            </Grid>
            <Grid item xs={1}>
              <TooltipOne
                placement="top"
                title={
                  isTotalLimitOutOfTolerance
                    ? 'Indywidualny całkowity limit poza tolerancją jest teraz włączony'
                    : 'Indywidualny całkowity limit poza tolerancją jest teraz wyłączony'
                }
              >
                <span>
                  <SwitchOne
                    disableRipple
                    checked={isTotalLimitOutOfTolerance}
                    className={classes.switch}
                    color={'primary'}
                    onChange={() => {
                      setIsTotalLimitOutOfTolerance(!isTotalLimitOutOfTolerance);
                      if (isTotalLimitOutOfTolerance) {
                        setTotalLimitOutOfTolerance(null);
                      }
                    }}
                    inputProps={{ 'aria-label': 'Indywidualny całkowity limit poza tolerancją' }}
                  />
                </span>
              </TooltipOne>
            </Grid>

            <Grid item xs={2} className={classes.limitInputField}>
              <TextField
                value={totalLimitOutOfTolerance ?? ''}
                onChange={handleTotalLimitOutOfToleranceChange}
                id="totalLimitOutOfTolerance"
                variant="outlined"
                fullWidth={true}
                disabled={!isTotalLimitOutOfTolerance}
                type="number"
                error={isTotalLimitOutOfToleranceError}
                aria-label={`Całkowity limit poza tolerancją dla ${props.limits?.lastName} ${props.limits?.firstName}`}
                inputProps={{
                  style: {
                    padding: '10px',
                    color: '#2C2D3E',
                    fontSize: '1rem',
                  },
                  min: MIN_TOTAL_LIMIT,
                  max: MAX_LIMIT,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <div className={classes.errorRow}>
            {isTotalLimitOutOfToleranceError && getErrorRow(ErrorMessage.totalLimitErrorMessage)}
            {isTotalLimitOutOfToleranceNotInRange && getErrorRow(ErrorMessage.totalLimitOutsideAllowedRange)}
          </div>

          <Grid item xs={12}>
            <hr className={classes.line}></hr>
          </Grid>

          <Grid item className={classes.item}>
            <Grid item xs={8}>
              <div className={classes.limitName}>Sekwencyjny limit poza tolerancją</div>
            </Grid>
            <Grid item xs={1}>
              <TooltipOne
                placement="top"
                title={
                  isSequentialLimitOutOfTolerance
                    ? 'Indywidualny sekwencyjny limit poza tolerancją jest teraz włączony'
                    : 'Indywidualny sekwencyjny limit poza tolerancją jest teraz wyłączony'
                }
              >
                <span>
                  <SwitchOne
                    disableRipple
                    checked={isSequentialLimitOutOfTolerance}
                    className={classes.switch}
                    color={'primary'}
                    onChange={() => {
                      setIsSequentialLimitOutOfTolerance(!isSequentialLimitOutOfTolerance);
                      if (isSequentialLimitOutOfTolerance) {
                        setSequentialLimitOutOfTolerance(null);
                      }
                    }}
                    inputProps={{ 'aria-label': 'Indywidualny sekwencyjny limit poza tolerancją' }}
                  />
                </span>
              </TooltipOne>
            </Grid>

            <Grid item xs={2} className={classes.limitInputField}>
              <TextField
                value={sequentialLimitOutOfTolerance ?? ''}
                onChange={handleSequentialLimitOutOfTolerance}
                id="sequentialLimitOutOfTolerance"
                variant="outlined"
                fullWidth={true}
                disabled={!isSequentialLimitOutOfTolerance}
                type="number"
                error={isSequentialLimitOutOfToleranceError}
                aria-label={`Sekwencyjny limit poza tolerancją dla ${props.limits?.lastName} ${props.limits?.firstName}`}
                inputProps={{
                  style: {
                    padding: '10px',
                    color: '#2C2D3E',
                    fontSize: '1rem',
                  },
                  min: MIN_SEQUENTIAL_LIMIT,
                  max: MAX_LIMIT,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <div className={classes.errorRow}>
            {isSequentialLimitOutOfToleranceError && getErrorRow(ErrorMessage.sequentialErrorMessage)}
            {isSequentialLimitOutOfToleranceNotInRange && getErrorRow(ErrorMessage.sequentialLimitOutsideAllowedRange)}
          </div>

          <Grid item xs={12}>
            <hr className={classes.line}></hr>
          </Grid>

          <Grid item className={classes.item}>
            <Grid item xs={8}>
              <div className={classes.limitName}>Całkowity limit w zakresie tolerancji</div>
            </Grid>
            <Grid item xs={1}>
              <TooltipOne
                placement="top"
                title={
                  isTotalLimitWithinTolerance
                    ? 'Indywidualny całkowity limit w zakresie tolerancji jest teraz włączony'
                    : 'Indywidualny całkowity limit w zakresie tolerancji jest teraz wyłączony'
                }
              >
                <span>
                  <SwitchOne
                    disableRipple
                    checked={isTotalLimitWithinTolerance}
                    className={classes.switch}
                    color={'primary'}
                    onChange={() => {
                      setIsTotalLimitWithinTolerance(!isTotalLimitWithinTolerance);
                      if (isTotalLimitWithinTolerance) {
                        setTotalLimitWithinTolerance(null);
                      }
                    }}
                    inputProps={{ 'aria-label': 'Indywidualny całkowity limit w zakresie tolerancji' }}
                  />
                </span>
              </TooltipOne>
            </Grid>

            <Grid item xs={2} className={classes.limitInputField}>
              <TextField
                value={totalLimitWithinTolerance ?? ''}
                onChange={handleTotalLimitWithinTolerance}
                id="totalLimitWithinTolerance"
                variant="outlined"
                fullWidth={true}
                disabled={!isTotalLimitWithinTolerance}
                type="number"
                error={isTotalLimitWithinToleranceError}
                aria-label={`Całkowity limit w zakresie tolerancji dla ${props.limits?.lastName} ${props.limits?.firstName}`}
                inputProps={{
                  style: {
                    padding: '10px',
                    color: '#2C2D3E',
                    fontSize: '1rem',
                  },
                  min: MIN_TOTAL_LIMIT,
                  max: MAX_LIMIT,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <div className={classes.errorRow}>
            {isTotalLimitWithinToleranceError && getErrorRow(ErrorMessage.totalLimitErrorMessage)}
            {isTotalLimitWithinToleranceNotInRange && getErrorRow(ErrorMessage.totalLimitOutsideAllowedRange)}
          </div>

          <Grid item xs={12}>
            <hr className={classes.line}></hr>
          </Grid>

          <Grid item className={classes.item}>
            <Grid item xs={8}>
              <div className={classes.limitName}>Sekwencyjny limit w zakresie tolerancji</div>
            </Grid>
            <Grid item xs={1}>
              <TooltipOne
                placement="top"
                title={
                  isSequentialLimitWithinTolerance
                    ? 'Indywidualny sekwencyjny limit w zakresie tolerancji jest teraz włączony'
                    : 'Indywidualny sekwencyjny limit w zakresie tolerancji jest teraz wyłączony'
                }
              >
                <span>
                  <SwitchOne
                    disableRipple
                    checked={isSequentialLimitWithinTolerance}
                    className={classes.switch}
                    color={'primary'}
                    onChange={() => {
                      setIsSequentialLimitWithinTolerance(!isSequentialLimitWithinTolerance);
                      if (isSequentialLimitWithinTolerance) {
                        setSequentialLimitWithinTolerance(null);
                      }
                    }}
                    inputProps={{ 'aria-label': 'Indywidualny sekwencyjny limit w zakresie tolerancji' }}
                  />
                </span>
              </TooltipOne>
            </Grid>

            <Grid item xs={2} className={classes.limitInputField}>
              <TextField
                value={sequentialLimitWithinTolerance ?? ''}
                onChange={handleSequentialLimitWithinTolerance}
                id="sequentialLimitWithinTolerance"
                variant="outlined"
                fullWidth={true}
                disabled={!isSequentialLimitWithinTolerance}
                type="number"
                error={isSequentialLimitWithinToleranceError}
                aria-label={`Sekwencyjny limit w zakresie tolerancji dla ${props.limits?.lastName} ${props.limits?.firstName}`}
                inputProps={{
                  style: {
                    padding: '10px',
                    color: '#2C2D3E',
                    fontSize: '1rem',
                  },
                  min: MIN_SEQUENTIAL_LIMIT,
                  max: MAX_LIMIT,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>

          <div className={classes.errorRow}>
            {isSequentialLimitWithinToleranceError && getErrorRow(ErrorMessage.sequentialErrorMessage)}
            {isSequentialLimitWithinToleranceNotInRange && getErrorRow(ErrorMessage.sequentialLimitOutsideAllowedRange)}
          </div>

          <Grid item xs={12}>
            <hr className={classes.line}></hr>
          </Grid>
        </Grid>
      </ModalWithButtons>
    </>
  );
};
